import type { EntityHighlight } from "./agentTypes";
import { entityLabel } from "./confirmationCardFormatting";
import type { ToolResult } from "./tools";

export const MAX_TURN_ENTITY_HIGHLIGHTS = 3;

export type EntityHighlightKind = "contract" | "event";

// Shared with callHandlers.ts so the two agent modules that need to check "is this a bare object,
// not an array or null" don't each keep their own copy.
export const isPlainRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const toHighlight = (entity: Record<string, unknown> | undefined): EntityHighlight | null => {
  if (!entity || typeof entity.id !== "string") {
    return null;
  }
  const label = entityLabel(entity);
  if (!label) {
    return null;
  }
  // referenceId is entityLabel's last-resort fallback (see ENTITY_LABEL_KEYS), so when nothing more
  // specific (title/name/subject) exists, referenceId IS the label — showing it again as the sublabel
  // would just duplicate the same string underneath itself.
  const referenceId = typeof entity.referenceId === "string" ? entity.referenceId : undefined;
  return {
    id: entity.id,
    label,
    sublabel: referenceId && referenceId !== label ? referenceId : undefined,
  };
};

// (a) The primary object touched by a successful gated mutation: the mutation's own result when it
// carries an id (e.g. a create/update response echoing the entity), else the args' contractId
// resolved through the entity index (an update whose response doesn't echo the full entity).
export const highlightFromMutationResult = (
  result: ToolResult,
  args: Record<string, unknown>,
  entityIndex: Map<string, Record<string, unknown>>,
): EntityHighlight | null => {
  if (!result.ok) {
    return null;
  }
  if (isPlainRecord(result.data) && typeof result.data.id === "string") {
    return toHighlight(entityIndex.get(result.data.id) ?? result.data);
  }
  const contractId = typeof args.contractId === "string" ? args.contractId : undefined;
  return contractId ? toHighlight(entityIndex.get(contractId)) : null;
};

// (b) A read that resolved to exactly one entity — never a list or a wrapper-of-a-list, which is why
// this only ever looks at a non-array object with its own id + name/title-ish field. Prefers the
// indexed copy over the raw result: getSourcingEvent returns an untagged payload that nests its
// title under eventData — indexEntity (see callHandlers.ts) already resolved that into the one shape
// every consumer expects.
export const highlightFromReadResult = (
  result: ToolResult,
  entityIndex: Map<string, Record<string, unknown>>,
): EntityHighlight | null => {
  if (!result.ok || !isPlainRecord(result.data)) {
    return null;
  }
  const id = result.data.id;
  return toHighlight(typeof id === "string" ? entityIndex.get(id) ?? result.data : result.data);
};

// Dedupe by id and cap at MAX_TURN_ENTITY_HIGHLIGHTS, preserving first-seen order. Called once per
// executed call so a turn's card row only ever grows, never reorders or duplicates.
export const addEntityHighlight = (
  existing: EntityHighlight[],
  next: EntityHighlight | null,
): EntityHighlight[] => {
  if (!next || existing.length >= MAX_TURN_ENTITY_HIGHLIGHTS || existing.some((e) => e.id === next.id)) {
    return existing;
  }
  return [...existing, next];
};

// Trivial, best-effort type sniff used only to pick a card icon (never for navigation, which stays
// on resolveEntityNavigation's __agentEntityKind tag) — contract vs event are the only two shapes
// it's worth guessing at; anything else renders without an icon.
export const inferEntityHighlightKind = (entity: Record<string, unknown> | undefined): EntityHighlightKind | undefined => {
  if (!entity) {
    return undefined;
  }
  if (entity.__agentEntityKind === "v2Event" || isPlainRecord(entity.eventData)) {
    return "event";
  }
  if (entity.__agentEntityKind === "contract" || typeof entity.state === "string") {
    return "contract";
  }
  return undefined;
};
